import {
  Box,
  Card,
  CenteredLoader,
  SemiDonut,
  SemiDonutProps,
  Separator,
  SubTitle,
  TooltipItem,
  TooltipModel,
  useTheme,
} from "@shared/primitive-components"
import { useMemo } from "react"
import { FleetStats } from "../../../../api/fleet"
import * as styles from "../styles/fleetListChartStyles"

export type CoverStatsDoughnutProps = {
  outerLoading: boolean
  stats: FleetStats | null
}

const ProtectedCoverStatsDoughnut = ({ stats }: { stats: FleetStats }) => {
  const theme = useTheme()
  const chart = useMemo<SemiDonutProps<Number, String> | undefined>(() => {
    const data = [
      {
        label: `On Cover`,
        value: stats.onCoverTotal,
        percentage: Number(stats.onCoverPercent.toFixed(0)),
        color: theme.palette.brand,
      },
      {
        label: `Off Cover`,
        value: stats.offCoverTotal,
        percentage: Number(stats.offCoverPercent.toFixed(0)),
        color: theme.palette.coal.light,
      },
      {
        label: `Scheduled`,
        value: stats.scheduledTotal,
        percentage: Number(stats.scheduledPercent.toFixed(0)),
        color: theme.palette.grey.dark,
      },
    ]

    const toolTipTitleFn = function (this: TooltipModel<"doughnut">, tooltipItem: TooltipItem<"doughnut">[]) {
      const { dataIndex } = tooltipItem[0]
      return `${data[dataIndex].percentage}% of all vehicles `
    }

    const toolTipLabelFn = function (this: TooltipModel<"doughnut">, tooltipItem: TooltipItem<"doughnut">) {
      const { dataIndex, parsed } = tooltipItem
      return dataIndex === 0
        ? `${parsed} vehicles on cover`
        : dataIndex === 1
        ? `${parsed} vehicles off cover`
        : `${parsed} vehicles scheduled`
    }

    return {
      data,
      toolTipLabelFn,
      toolTipTitleFn,
    }
  }, [stats])

  if (!chart) return null

  return (
    <SemiDonut<Number, String>
      title="Insurance status"
      subtitle="Here's a summary of the insurance status of all the vehicles added to your fleet. Add new vehicles and edit cover dates using the table below."
      chart={chart}
      underChart={{
        value: stats.totalVehicles.toString(),
        label: `Vehicles`,
      }}
    />
  )
}

export const CoverStatsDoughnut = ({ stats, outerLoading }: CoverStatsDoughnutProps) => {
  if (!stats || outerLoading) {
    return (
      <Card css={styles.cardOuterContainer}>
        <Box css={styles.cardContainer}>
          <Box css={styles.marginBottom}>
            <SubTitle css={styles.title}>INSURANCE STATUS</SubTitle>
            <Separator size="block" color="yellow" />
          </Box>
          <Box css={styles.loaderContainer}>
            <CenteredLoader />
          </Box>
        </Box>
      </Card>
    )
  }

  return <ProtectedCoverStatsDoughnut stats={stats} />
}
